<template lang="pug">

.wrap(:key='activeCompany ? activeCompany.id : "superadmin"')
  page-title Your weekly roundup

  template(v-if='!loading')
    .section
      el-row(:gutter='20')
        el-col(:sm='12')
          .section-title Programs
          .section-content(v-loading='loading')
            .overall-stats
              .circle-wrap
                .stat-panel
                  circular-counter(id='passedPrograms' label='passed this week' :total='stats.overallProgramsCount.count' :count='stats.passedProgramsCount.count' color='#6fc9be')
                .stat-panel
                  circular-counter(id='failedPrograms' label='failed this week' :total='stats.overallProgramsCount.count' :count='stats.failedProgramsCount.count' color='#f25b3f')

        el-col(:sm='12')
          .section-title Courses
          .section-content(v-loading='loading')
            .overall-stats
              .circle-wrap
                .stat-panel
                  circular-counter(id='passedCourses' label='passed this week' :total='stats.overallCoursesCount.count' :count='stats.passedCoursesCount.count' color='#6fc9be')
                .stat-panel
                  circular-counter(id='failedCourses' label='failed this week' :total='stats.overallCoursesCount.count' :count='stats.failedCoursesCount.count' color='#f25b3f')

      chart-wrap
        line-chart(v-if="$mq.resize && $mq.above(500)" :chart-data="lineChartData" :options='chartOptions' :height='200')



</template>

<script>
import { mapGetters } from "vuex";
import setHours from "date-fns/set_hours";
import setMinutes from "date-fns/set_minutes";
import ChartWrap from "@/shared/components/ChartWrap";
import PageTitle from "@/shared/components/layout/PageTitle";
import CircularCounter from "@/shared/components/CircularCounter";
import format from "date-fns/format";
import eachDay from "date-fns/each_day";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import DashboardEmpty from "../DashboardEmpty";

import LineChart from "./LineChart";

const formatApiDate = date => format(date, "YYYY-MM-DDTHH:mm");
const setTime = (date, hours, minutes) =>
  setMinutes(setHours(date, hours), minutes);

export default {
  components: {
    PageTitle,
    CircularCounter,
    ChartWrap,
    DashboardEmpty,
    LineChart
  },
  data() {
    const now = Date.now();
    const start = setTime(startOfWeek(now, { weekStartsOn: 1 }), 0, 1);
    const end = setTime(endOfWeek(now, { weekStartsOn: 1 }), 23, 59);
    const apiDateRange = [formatApiDate(start), formatApiDate(end)];

    return {
      loading: false,
      stats: {
        overallProgramsCount: {
          count: 0
        },
        passedProgramsCount: {
          count: 0
        },
        failedProgramsCount: {
          count: 0
        },

        overallCoursesCount: {
          count: 0
        },
        passedCoursesCount: {
          count: 0
        },
        failedCoursesCount: {
          count: 0
        },
        passedCoursesPerDay: [],
        failedCoursesPerDay: []
      },
      apiDateRange,
      dateRange: eachDay(start, end)
    };
  },
  watch: {
    "activeCompany.id": function watchActiveCompany() {
      this.loadReport();
    }
  },
  computed: {
    ...mapGetters({
      activeCompany: "auth/activeCompany"
    }),
    lineChartData() {
      const groups = [
        {
          data: this.stats.passedCoursesPerDay,
          label: "Passed courses",
          color: "#6fc9be"
        },
        {
          data: this.stats.failedCoursesPerDay,
          label: "Failed courses",
          color: "#f25b3f"
        }
      ];

      const datasets = groups.map(group => ({
        fill: false,
        ...group.chartOptions,
        pointRadius: 4,
        lineTension: 0,
        borderColor: group.color,
        borderWidth: 2,
        label: group.label,
        data: this.dateRange.map(date => {
          const formattedDate = format(date, "YYYY-MM-DD");
          const statEntry = group.data.find(d => d.day === formattedDate);
          return statEntry ? statEntry.count : 0;
        })
      }));

      const labels = this.dateRange.map(d => format(d, "D MMM"));

      return {
        labels,
        datasets
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        layout: {
          padding: 20
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: true,
                color: "rgba(0, 0, 0, 0.03)"
              }
            }
          ]
        }
      };
    }
  },
  methods: {
    async loadReport() {
      const params = {
        company: this.activeCompany.id,
        start: this.apiDateRange[0],
        end: this.apiDateRange[1]
      };

      this.loading = true;

      const stats = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: "/itrain/reports/overall/dashboard/",
        params
      });

      this.stats = { ...this.stats, ...stats };

      this.loading = false;
    }
  },
  created() {
    this.loadReport();
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../assets/scss/mixins.scss";
.section {
  background: white;
  padding: 30px;
  margin-bottom: 30px;
}

.section-title {
  font-size: 30px;
  font-weight: 400;
  margin-bottom: 10px;
  text-align: center;
}

.section-content {
  &.center {
    display: flex;
    justify-content: center;
  }
}

.overall-stats {
  display: flex;
  align-items: stretch;
  .num {
    font-size: 50px;
    margin-bottom: 5px;
  }

  .count {
    width: 25%;
    background: white;
    padding: 20px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
  .circle-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    .stat-panel {
      flex-basis: calc(100% / 3 - 10px);
      background: white;
      padding: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      .label {
        font-size: 12px;
      }
    }
  }
  @include MQ(L) {
    flex-direction: column;
    align-items: center;
    .count {
      width: 100%;
    }
    .circle-wrap {
      width: 100%;
      justify-content: space-around;
    }
  }
  @include MQ(S) {
    .circle-wrap {
      flex-wrap: wrap;
      justify-content: center;
    }
  }
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  min-height: 200px;
}

.circles {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  @include MQ(M) {
    flex-wrap: wrap;
    justify-content: center;
  }
}

.circle {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-basis: calc(25% - 10px);
  @include MQ(M) {
    flex-basis: calc(50% - 10px);
  }
  @include MQ(XS) {
    flex-basis: 100%;
  }
}
</style>
